import { useState, useMemo } from 'react';
import { SignatureModel } from '../../../../types';
import SimpleDrawer from '../../../shared/simple-drawer';
import { useUserSettings } from '../../../utils/use-user-settings';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import { DisabledTooltipMenuItem } from '../../../shared/datagrids/tooltip-grid-actions-cell-item';
import SharedSignatureTemplateGridLogs from './shared-signature-template-grid-logs';
import ModelDetailsDisplay from '../../../shared/model-details-display';
import HtmlPreview from '../../../shared/html-preview';
import AvatarChip from '../../../shared/avatar-chip';
import { useStore } from '../../state/zustand-store';
import DeleteSignatureTemplateDialog from './delete-signature-template-dialog';
import { useSignatureTemplateActions } from './hooks/use-signature-template-actions';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAllSignatures, UserKeys } from '../../../api/users-api';

import Box from '@mui/material/Box';
import ListItemIcon from '@mui/material/ListItemIcon';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import { useTheme } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import Edit from '@mui/icons-material/Edit';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
import MoreVert from '@mui/icons-material/MoreVert';
import Update from '@mui/icons-material/Update';
import Check from '@mui/icons-material/Check';
import Delete from '@mui/icons-material/Delete';
import ContentCopy from '@mui/icons-material/ContentCopy';
import CloseOutlined from '@mui/icons-material/CloseOutlined';

interface SignatureTemplateDrawerContentProps {
  selectedTemplate: SignatureModel;
  canEdit?: boolean;
  canClone?: boolean;
  canDelete?: boolean;
  onDeleteSuccess?: () => void;
}

function SignatureTemplateDrawerContent({
  selectedTemplate,
  canEdit = true,
  canClone = true,
  canDelete = true,
  onDeleteSuccess,
}: SignatureTemplateDrawerContentProps) {
  const { toolsRoles, changeLogRoles } = useUserSettings();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const {
    closeDrawer,
    handleEditTemplate,
    handleCloneTemplate,
    handleDeleteTemplate,
    openDeleteDialog,
    closeDeleteDialog,
    templateForDelete,
  } = useSignatureTemplateActions();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [tabValue, setTabValue] = useState(0);
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const canEditUI = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.EditTemplate.value
  );
  const canCloneUI = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CreateTemplates.value
  );
  const canDeleteUI = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.DeleteTemplates.value
  );

  const formatDate = (date: string | number | null | undefined) => {
    if (!date || !dayjs(date).isValid()) return 'Never';
    return dayjs(date).format('MMM D, YYYY h:mm A');
  };

  const basicInfoData: Record<string, React.ReactNode> = useMemo(() => {
    const emailsToNotify = selectedTemplate.emailsToNotify || [];

    return {
      'Manual Run': selectedTemplate.manualRun ? (
        <Check sx={{ color: 'text.secondary' }} fontSize="small" />
      ) : (
        <CloseOutlined sx={{ color: 'text.secondary' }} fontSize="small" />
      ),
      'Scheduled Run': selectedTemplate.scheduled ? (
        <Check sx={{ color: 'text.secondary' }} fontSize="small" />
      ) : (
        <CloseOutlined sx={{ color: 'text.secondary' }} fontSize="small" />
      ),
      'Apply to Only Primary Send As Address':
        selectedTemplate.runOnPrimaryAlias ? (
          <Check sx={{ color: 'text.secondary' }} fontSize="small" />
        ) : (
          <CloseOutlined sx={{ color: 'text.secondary' }} fontSize="small" />
        ),
      'Use send as account for signature template':
        selectedTemplate.useSendAsAccountForTemplate ? (
          <Check sx={{ color: 'text.secondary' }} fontSize="small" />
        ) : (
          <CloseOutlined sx={{ color: 'text.secondary' }} fontSize="small" />
        ),
      'Emails to Notify':
        emailsToNotify.length > 0 ? (
          <Stack direction="row" gap={1} flexWrap="wrap">
            {emailsToNotify.map((email) => (
              <AvatarChip key={email} email={email} type="User" size="small" />
            ))}
          </Stack>
        ) : (
          'N/A'
        ),
    };
  }, [selectedTemplate]);

  const targetsData: Record<string, React.ReactNode> = useMemo(() => {
    const sourceTypeDisplay =
      selectedTemplate.sourceType
        ?.replaceAll('_', ' ')
        ?.replace(/([A-Z])/g, ' $1')
        .trim() || 'N/A';

    const sources = selectedTemplate.sources || [];
    const sourcesDisplay =
      sources.length > 0 ? (
        <Stack direction="row" gap={1} flexWrap="wrap">
          {sources.map((source) => (
            <AvatarChip
              key={source}
              label={source}
              email={source}
              type="User"
              size="small"
            />
          ))}
        </Stack>
      ) : (
        'N/A'
      );

    return {
      'Target Type': sourceTypeDisplay,
      'Target(s)': sourcesDisplay,
    };
  }, [selectedTemplate]);

  const scheduleData: Record<string, React.ReactNode> | null = useMemo(() => {
    if (!selectedTemplate.scheduled) {
      return null;
    }

    const times = selectedTemplate.times || [];
    const dates = selectedTemplate.dates || [];
    const endDate = selectedTemplate.scheduleEndDate;

    const formattedTimes = times.map((time) => {
      const t = Number.parseInt(time, 10);
      if (t === 0) return '12 AM';
      if (t < 12) return `${t} AM`;
      if (t === 12) return '12 PM';
      return `${t - 12} PM`;
    });

    return {
      Times: formattedTimes.length > 0 ? formattedTimes.join(', ') : 'N/A',
      Dates: dates.length > 0 ? dates.join(', ') : 'N/A',
      'Expiration Date': formatDate(endDate),
    };
  }, [selectedTemplate]);

  const theme = useTheme();

  const { setSnackbar } = useStore();

  return (
    <>
      <SimpleDrawer title="Signature Template Details" onClose={closeDrawer}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" sx={{ mt: -2, fontWeight: 'bold' }}>
            {selectedTemplate.templateName || 'N/A'}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title="Actions">
              <IconButton onClick={handleClick}>
                <MoreVert />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={open}
              elevation={1}
              onClose={handleClose}
            >
              <DisabledTooltipMenuItem
                onClick={() => {
                  handleEditTemplate(selectedTemplate);
                  handleClose();
                }}
                disabled={!canEditUI || !canEdit}
                data-disabled-reason={
                  !canEditUI || !canEdit
                    ? Messages.Errors.Permission.Acting(
                        ToolsRoles.EditTemplate.name
                      )
                    : undefined
                }
              >
                <ListItemIcon>
                  <Edit fontSize="small" />
                </ListItemIcon>
                Edit
              </DisabledTooltipMenuItem>
              <DisabledTooltipMenuItem
                onClick={() => {
                  handleCloneTemplate(selectedTemplate);
                  handleClose();
                }}
                disabled={!canCloneUI || !canClone}
                data-disabled-reason={
                  !canCloneUI || !canClone
                    ? Messages.Errors.Permission.Acting(
                        ToolsRoles.CreateTemplates.name
                      )
                    : undefined
                }
              >
                <ListItemIcon>
                  <LibraryAdd fontSize="small" />
                </ListItemIcon>
                Clone
              </DisabledTooltipMenuItem>
              <DisabledTooltipMenuItem
                onClick={() => {
                  handleDeleteTemplate(selectedTemplate);
                  handleClose();
                }}
                disabled={!canDeleteUI || !canDelete}
                data-disabled-reason={
                  !canDeleteUI || !canDelete
                    ? Messages.Errors.Permission.Acting(
                        ToolsRoles.DeleteTemplates.name
                      )
                    : undefined
                }
              >
                <ListItemIcon>
                  <Delete fontSize="small" />
                </ListItemIcon>
                Delete
              </DisabledTooltipMenuItem>
            </Menu>
          </Stack>
        </Stack>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: -2, mb: 2 }}
        >
          {selectedTemplate.description}
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2 }}
          sx={{ mb: 2, alignItems: 'center' }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ color: 'text.secondary' }}
          >
            <Update fontSize="small" />
            <Typography variant="body2" color="inherit">
              Last Run Date: {formatDate(selectedTemplate.lastRunDate)}
            </Typography>
          </Stack>
          <Typography sx={{ px: 1, color: 'text.secondary', fontSize: 14 }}>
            •
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ color: 'text.secondary' }}
          >
            <Check fontSize="small" />
            <Typography variant="body2" color="inherit">
              Created: {formatDate(selectedTemplate.createdDate)}
            </Typography>
          </Stack>
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="signature template details tabs"
          >
            <Tab label="Overview" />
            <Tab label="Logs" />
          </Tabs>
        </Box>

        <Box sx={{ pt: 1, pb: 4 }}>
          {tabValue === 0 && (
            <>
              <ModelDetailsDisplay
                title="Basic Information"
                data={basicInfoData}
              />
              <ModelDetailsDisplay title="Targets" data={targetsData} />
              {scheduleData && (
                <ModelDetailsDisplay title="Schedule" data={scheduleData} />
              )}
              {selectedTemplate.templateHtml && (
                <Box sx={{ mt: 2 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      display: 'flex',
                      mb: 1,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 'bold', fontSize: 14 }}
                      >
                        Template Preview
                      </Typography>
                    </Stack>
                    <Tooltip title="Copy">
                      <IconButton
                        size="small"
                        onClick={async (e) => {
                          e.stopPropagation();

                          if (navigator.clipboard) {
                            await navigator.clipboard.writeText(
                              selectedTemplate.templateHtml || ''
                            );
                            setSnackbar({
                              open: true,
                              severity: 'info',
                              message:
                                'Template Html has been copied to clipboard.',
                            });
                          }
                        }}
                        sx={{
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <HtmlPreview
                    html={selectedTemplate.templateHtml}
                    showTitle={false}
                    minHeight={1}
                  />
                </Box>
              )}
            </>
          )}
          {tabValue === 1 && (
            <SharedSignatureTemplateGridLogs
              signatureId={selectedTemplate.id}
            />
          )}
        </Box>
      </SimpleDrawer>

      {templateForDelete && openDeleteDialog && (
        <DeleteSignatureTemplateDialog
          open={openDeleteDialog}
          handleClose={() => {
            closeDeleteDialog();
          }}
          selectedRows={[templateForDelete.id]}
          setSelectedRows={() => {}}
          onDeleteSuccess={() => {
            onDeleteSuccess?.();
          }}
          changeLogRoles={changeLogRoles}
          signatures={[templateForDelete]}
        />
      )}
    </>
  );
}

/**
 * Global, self-contained signature template detail drawer.
 * Driven entirely by the `?selectedTemplate=<id>` URL search param.
 * Place this once at the App level — no props required.
 */
export default function SignatureTemplateDrawerDetailsOld() {
  const [searchParams] = useSearchParams();
  const { closeDrawer } = useSignatureTemplateActions();

  const selectedTemplateId = searchParams.get('selectedTemplate');
  const { data: signatures, isLoading } = useQuery({
    queryKey: [UserKeys.Signatures],
    queryFn: () => getAllSignatures(),
    enabled: !!selectedTemplateId,
  });

  const selectedSignatureTemplate =
    signatures?.find((s) => s.id.toString() === selectedTemplateId) ?? null;

  const isDrawerOpen = !!selectedTemplateId && !isLoading;

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={closeDrawer}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100vw', md: '45%' },
        },
      }}
    >
      {selectedSignatureTemplate && (
        <SignatureTemplateDrawerContent
          selectedTemplate={selectedSignatureTemplate}
          onDeleteSuccess={closeDrawer}
        />
      )}
    </Drawer>
  );
}
